<template>
  <coozzy-card :title="$t('message.marketingSettings.automaticReferenceCheck.title')">
    <b-card-sub-title>
      {{ $t('message.marketingSettings.automaticReferenceCheck.subTitle') }}
    </b-card-sub-title>
    <div
      v-if="loading"
      class="row">
      <div class="col text-center">
        <coozzy-spinner />
      </div>
    </div>
    <template v-else>
      <div class="row mt-3">
        <div class="col-sm-6 col-md-4 mb-2">
          <label>{{ $t('message.marketingSettings.automaticReferenceCheck.query') }}</label>
          <coozzy-form-select
            v-model="queryMode"
            :disabled="readOnly">
            <option :value="queryModes.NONE">
              {{ $t('message.marketingSettings.automaticReferenceCheck.queryNone') }}
            </option>
            <option :value="queryModes.AFFORDABILITY">
              {{ $t('message.marketingSettings.automaticReferenceCheck.queryAffordability') }}
            </option>
            <option :value="queryModes.COMPLETE_DOSSIER_AND_AFFORDABILITY">
              {{ $t('message.marketingSettings.automaticReferenceCheck.queryCompleteDossier') }}
            </option>
          </coozzy-form-select>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-sm-6 col-md-4 mb-2">
          <label>{{ $t('message.marketingSettings.automaticReferenceCheck.senderEmail') }}{{ isSenderEmailRequired ? '*' : '' }}</label>
          <coozzy-form-select
            v-model="senderMailboxId"
            :disabled="readOnly"
            :state="$v.senderMailboxId && $v.senderMailboxId.$dirty && $v.senderMailboxId.$error ? false : null">
            <option value="">
              -
            </option>
            <option
              v-for="mailbox in mailBoxList"
              :key="mailbox.id"
              :value="mailbox.id">
              {{ mailbox.outboundEmail }}
            </option>
          </coozzy-form-select>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <coozzy-button
            v-if="isCompanyAdmin"
            design="primary"
            class="float-right"
            @click="saveClicked">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </div>
    </template>
  </coozzy-card>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import CoozzyCard from '@/framework/components/card/CoozzyCard'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import MessageApi from '@/misc/apis/MessageApi'
import { user } from '@/mixins/user'
import Vue from 'vue'
import { REFERENCE_CHECK_QUERY_MODES } from '@/types/models'
import { loadReferenceCheckSettings, saveReferenceCheckSettings } from '@/settings/marketing/referenceCheckSettings'

export default {
  name: 'AutomaticReferenceCheckSettings',
  components: {
    CoozzySpinner,
    CoozzyButton,
    CoozzyFormSelect,
    CoozzyCard
  },
  mixins: [user],
  data() {
    return {
      loading: false,
      queryMode: REFERENCE_CHECK_QUERY_MODES.NONE,
      senderMailboxId: '',
      mailBoxList: [],
      queryModes: REFERENCE_CHECK_QUERY_MODES
    }
  },
  computed: {
    readOnly() {
      return !this.isCompanyAdmin
    },
    isSenderEmailRequired() {
      return this.queryMode !== REFERENCE_CHECK_QUERY_MODES.NONE
    }
  },
  watch: {
    queryMode() {
      if (this.$v) {
        this.$v.$reset()
      }
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      Promise.all([
        this.loadMailboxes(),
        this.loadSettings()
      ]).then(() => {
        this.reconcileSelectedMailbox()
      }).finally(() => {
        this.loading = false
      })
    },
    loadMailboxes() {
      const request = this.isCompanyAdmin
        ? MessageApi.listMailboxes(this.accountId)
        : MessageApi.listMailboxesByUserId(this.$store.getters['user/getUserId'])
      return request
        .then(response => {
          this.mailBoxList = (response.mailboxes || []).filter(mailbox => mailbox.active)
        })
        .catch(() => {
          Vue.toasted.show(this.$t('message.loadingErrors.mailbox'), { type: 'error' })
        })
    },
    loadSettings() {
      return loadReferenceCheckSettings(this.accountId, this.currentLanguage)
        .then(settings => {
          this.queryMode = settings.queryMode
          this.senderMailboxId = settings.senderMailboxId || ''
        })
        .catch(() => {
          Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
        })
    },
    reconcileSelectedMailbox() {
      if (!this.senderMailboxId) {
        return
      }
      const stillActive = this.mailBoxList.some(mailbox => String(mailbox.id) === String(this.senderMailboxId))
      if (!stillActive) {
        this.senderMailboxId = ''
      }
    },
    saveClicked() {
      if (this.$v) {
        this.$v.$touch()
        if (this.$v.$invalid) {
          return
        }
      }

      this.loading = true
      saveReferenceCheckSettings(this.accountId, this.currentLanguage, {
        queryMode: this.queryMode,
        senderMailboxId: this.senderMailboxId || null
      }).then(() => {
        Vue.toasted.show(this.$t('message.marketingSettings.automaticReferenceCheck.savedSuccessfully'), { type: 'success' })
      }).catch(() => {
        Vue.toasted.show(this.$t('message.marketingSettings.automaticReferenceCheck.savedFailed'), { type: 'error' })
      }).finally(() => {
        this.loading = false
      })
    }
  },
  validations() {
    if (this.isSenderEmailRequired) {
      return {
        senderMailboxId: {
          required
        }
      }
    }
    return {}
  }
}
</script>
